import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";

/**
 * Which build the web server is running, from the unauthenticated liveness probe (2026-09-28).
 *
 * `buildCommit` is the commit the web image was stamped with (`Dockerfile.web`); `buildRef` is
 * the build the release deployed (session-3 decision 22): its version `YYYY.MM.DD.N-<sha7>`
 * since #873, a short commit on older releases, or a digest. Both are null on a local run.
 */
export const LIVENESS_PATH = "/api/health";

export interface ServerBuild {
  buildRef: string | null;
  buildVersion?: string | null;
  buildTime?: string | null;
  buildCommit?: string | null;
  /** Optional product features this deployment offers; absent on an older server. */
  features?: { tableRuns?: boolean };
}

/**
 * Re-read every five minutes and when the tab regains focus: the question is "was a release
 * deployed under this open tab", which only matters on the scale of minutes, and the probe
 * touches no dependency.
 */
const SERVER_BUILD_POLL_MS = 5 * 60_000;

export function useServerBuild() {
  return useQuery<ServerBuild>({
    queryKey: [LIVENESS_PATH],
    // The probe is unauthenticated: no token is fetched or sent for it.
    queryFn: ({ signal }) => apiRequest<ServerBuild>(LIVENESS_PATH, { signal, anonymous: true }),
    refetchInterval: SERVER_BUILD_POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
    retry: false,
  });
}

const COMMIT_PATTERN = /^[0-9a-f]{7,40}$/;
/** A build version, `YYYY.MM.DD.N-<sha7>` (#873): its last seven characters are the commit. */
const VERSION_PATTERN = /^\d{4}\.\d{2}\.\d{2}\.\d+-([0-9a-f]{7})$/;

/**
 * The server's commit: the image's own stamp when it has one (preferred: it is the full commit),
 * else the deployed build ref when that names a commit, a short commit or the sha7 of a version.
 * A digest (`sha256:...`) names no commit, so it answers null.
 */
export function serverCommitOf(build: ServerBuild | undefined): string | null {
  const stamped = build?.buildCommit?.trim().toLowerCase();
  if (stamped && COMMIT_PATTERN.test(stamped)) return stamped;
  const ref = build?.buildRef?.trim().toLowerCase();
  if (!ref) return null;
  if (COMMIT_PATTERN.test(ref)) return ref;
  return VERSION_PATTERN.exec(ref)?.[1] ?? null;
}

/**
 * Product gaps row 2: whether this deployment offers table-level runs (`SKYDATA_TABLE_RUNS`), from
 * the liveness probe the footer already reads. False until the probe answers, and on an older
 * server, so a control the API would refuse is never drawn.
 */
export function useTableRunsEnabled(): boolean {
  return useServerBuild().data?.features?.tableRuns === true;
}
